import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../shared/store';
import { nextStep, previousStep, skipTutorial, completeTutorial, completeStep, tutorialSliceAPI } from '../store/tutorialSlice';
import { TUTORIAL_STEPS } from '../store/tutorialSlice';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, StarIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('TutorialOverlay');

// Welcome Screen Component
function WelcomeScreen({ step, currentStep, totalSteps, onNext, onSkip }) {
    const features = [
        {
            icon: '⚙️',
            title: 'Easy Setup',
            description: 'Configure your store settings and branding in minutes',
        },
        {
            icon: '📱',
            title: 'Digital Wallet Passes',
            description: 'Create Apple Wallet and Google Wallet passes for your customers',
        },
        {
            icon: '🎁',
            title: 'Rewards System',
            description: 'Set up points, visits, and rewards for customer engagement',
        },
        {
            icon: '📊',
            title: 'Customer Management',
            description: 'Track customer activity, points, visits, and redemption history',
        },
        {
            icon: '📧',
            title: 'Marketing Campaigns',
            description: 'Send targeted email campaigns to engage your customer base',
        },
        {
            icon: '📈',
            title: 'Analytics Dashboard',
            description: 'Monitor your loyalty program performance with detailed analytics',
        },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pointer-events-auto">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2">
                        <StarIcon className="w-6 h-6 text-primary" />
                        <span className="text-sm font-semibold text-primary">
                            Step {currentStep + 1} of {totalSteps}
                        </span>
                    </div>
                    <button
                        onClick={onSkip}
                        className="text-gray-400 hover:text-gray-600 transition"
                        aria-label="Skip tutorial"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Title */}
                    <div className="text-center mb-6">
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">{step.title}</h2>
                        <p className="text-lg text-gray-600">{step.description}</p>
                    </div>

                    {/* Dashboard Preview Image */}
                    <div className="mb-8 rounded-lg overflow-hidden shadow-lg border-2 border-gray-200">
                        <img
                            src="/preview1.png"
                            alt="LoyalPass Dashboard Preview"
                            className="w-full h-auto"
                        />
                    </div>

                    {/* Features Grid */}
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                            Key Features of LoyalPass
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="text-2xl">{feature.icon}</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 mb-1">
                                                {feature.title}
                                            </h4>
                                            <p className="text-sm text-gray-600">
                                                {feature.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Call to Action */}
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                        <p className="text-center text-gray-700">
                            <strong>Ready to get started?</strong> Follow the tutorial to set up your loyalty program step by step.
                        </p>
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-end">
                        <button
                            onClick={onNext}
                            className="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90 transition flex items-center gap-2"
                        >
                            Get Started
                            <ChevronRightIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Completion Screen Component
function CompletionScreen({ step, currentStep, totalSteps, onFinish, onPrevious }) {
    const nextSteps = [
        {
            icon: '👥',
            title: 'Generate Your First Pass',
            description: 'Create a wallet pass for a customer and see how it works',
        },
        {
            icon: '🎁',
            title: 'Set Up Rewards',
            description: 'Create rewards that customers can redeem with their points',
        },
        {
            icon: '📧',
            title: 'Send a Campaign',
            description: 'Reach out to your customers with marketing campaigns',
        },
        {
            icon: '📊',
            title: 'Track Analytics',
            description: 'Monitor your loyalty program performance and customer engagement',
        },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pointer-events-auto">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2">
                        <TrophyIcon className="w-6 h-6 text-primary" />
                        <span className="text-sm font-semibold text-primary">
                            Step {currentStep + 1} of {totalSteps}
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Success Icon and Title */}
                    <div className="text-center mb-6">
                        <div className="flex justify-center mb-4">
                            <div className="rounded-full bg-green-100 p-4">
                                <CheckCircleIcon className="w-16 h-16 text-green-600" />
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">{step.title}</h2>
                        <p className="text-lg text-gray-600">{step.description}</p>
                    </div>

                    {/* What You've Accomplished */}
                    <div className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                            What You've Accomplished
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex items-center gap-2 text-gray-700">
                                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span>Configured your store settings</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span>Set up wallet pass templates</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span>Learned about rewards system</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span>Explored customer management</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span>Discovered marketing features</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span>Understood analytics dashboard</span>
                            </div>
                        </div>
                    </div>

                    {/* Next Steps */}
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                            What's Next?
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {nextSteps.map((nextStep, index) => (
                                <div
                                    key={index}
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="text-2xl">{nextStep.icon}</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 mb-1">
                                                {nextStep.title}
                                            </h4>
                                            <p className="text-sm text-gray-600">
                                                {nextStep.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Encouragement Message */}
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                        <p className="text-center text-gray-700">
                            <strong>You're all set!</strong> Start building your loyalty program and watch your customer engagement grow. Need help? Check out the tutorial button anytime.
                        </p>
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between gap-4">
                        <button
                            onClick={onPrevious}
                            className="px-4 py-2 rounded-md font-medium transition bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-2"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                            Previous
                        </button>
                        <button
                            onClick={onFinish}
                            className="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90 transition flex items-center gap-2"
                        >
                            Start Using LoyalPass
                            <CheckCircleIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Skip Tutorial Confirmation Modal
function SkipTutorialModal({ isOpen, onClose, onConfirm }) {
    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[9999] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Skip Tutorial</h3>
                <p className="text-gray-600 mb-6">
                    Are you sure you want to skip the tutorial? You can start it again later.
                </p>
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 rounded bg-[#3498db] hover:bg-[#2980b9] text-white transition"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function TutorialOverlay() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isActive, currentStep, completedSteps } = useAppSelector((state) => state.tutorial);
    const [showSkipModal, setShowSkipModal] = useState(false);

    const step = TUTORIAL_STEPS[currentStep];
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
    const isCurrentStepCompleted = completedSteps.includes(currentStep);

    // Navigate to the correct route when step changes (skip welcome and completion screens)
    useEffect(() => {
        if (isActive && step && !step.isWelcomeScreen && !step.isCompletionScreen && location.pathname !== step.route) {
            navigate(step.route);
        }
    }, [isActive, currentStep, navigate, location.pathname, step]);

    // Scroll to top when step changes (skip welcome and completion screens)
    useEffect(() => {
        if (isActive && step && !step.isWelcomeScreen && !step.isCompletionScreen) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [isActive, currentStep, step]);

    // Auto-complete steps that don't require tasks (viewing pages, welcome screen, and completion screen)
    useEffect(() => {
        if (isActive && step) {
            const viewingOnlySteps = ['marketing', 'customers', 'staff', 'welcome', 'completion'];
            if (viewingOnlySteps.includes(step.id) && !isCurrentStepCompleted) {
                // Automatically mark these steps as complete when navigated to
                dispatch(completeStep(currentStep));
            }
        }
    }, [isActive, currentStep, step, isCurrentStepCompleted, dispatch]);

    // Define handlers before using them
    const handleNext = async () => {
        if (isLastStep) {
            dispatch(completeTutorial());
            // Sync completion to database
            try {
                await dispatch(tutorialSliceAPI.updateTutorialStatus(true)).unwrap();
            } catch (error) {
                logger.error('Failed to sync tutorial completion to database', error);
            }
        } else {
            dispatch(nextStep());
        }
    };

    const handleMarkComplete = () => {
        dispatch(completeStep(currentStep));
    };

    const handlePrevious = () => {
        dispatch(previousStep());
    };

    const handleSkip = () => {
        setShowSkipModal(true);
    };

    const handleSkipConfirm = async () => {
        setShowSkipModal(false);
        dispatch(skipTutorial());
        // Sync completion to database
        try {
            await dispatch(tutorialSliceAPI.updateTutorialStatus(true)).unwrap();
        } catch (error) {
            logger.error('Failed to sync tutorial skip to database', error);
        }
    };

    if (!isActive || !step) {
        return null;
    }

    // Render welcome screen differently
    if (step.isWelcomeScreen) {
        return (
            <>
                {/* Skip Tutorial Confirmation Modal */}
                <SkipTutorialModal
                    isOpen={showSkipModal}
                    onClose={() => setShowSkipModal(false)}
                    onConfirm={handleSkipConfirm}
                />
                <WelcomeScreen step={step} currentStep={currentStep} totalSteps={TUTORIAL_STEPS.length} onNext={handleNext} onSkip={handleSkip} />
            </>
        );
    }

    // Render completion screen differently
    if (step.isCompletionScreen) {
        return (
            <>
                {/* Skip Tutorial Confirmation Modal */}
                <SkipTutorialModal
                    isOpen={showSkipModal}
                    onClose={() => setShowSkipModal(false)}
                    onConfirm={handleSkipConfirm}
                />
                <CompletionScreen step={step} currentStep={currentStep} totalSteps={TUTORIAL_STEPS.length} onFinish={handleNext} onPrevious={handlePrevious} />
            </>
        );
    }

    return (
        <>
            {/* Skip Tutorial Confirmation Modal */}
            <SkipTutorialModal
                isOpen={showSkipModal}
                onClose={() => setShowSkipModal(false)}
                onConfirm={handleSkipConfirm}
            />

            {/* Tutorial Card - Fixed position, allows page interaction */}
            <div className="fixed bottom-4 right-4 z-50 pointer-events-none">
                <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full pointer-events-auto border-2 border-primary">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-semibold text-primary">
                                    Step {currentStep + 1} of {TUTORIAL_STEPS.length}
                                </span>
                                {isCurrentStepCompleted && (
                                    <div className="flex items-center gap-1 text-green-600">
                                        <CheckCircleIcon className="w-5 h-5" />
                                        <span className="text-sm font-medium">Completed</span>
                                    </div>
                                )}
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                            {!isCurrentStepCompleted && (
                                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-yellow-800">
                                            <strong>Note:</strong> Please complete this task and mark it as done before continuing.
                                        </p>
                                        <button
                                            onClick={handleMarkComplete}
                                            className="ml-3 px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition whitespace-nowrap"
                                        >
                                            Mark as Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleSkip}
                            className="ml-4 text-gray-400 hover:text-gray-600 transition"
                            aria-label="Skip tutorial"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${((currentStep + 1) / TUTORIAL_STEPS.length) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex items-center justify-between gap-4">
                        <button
                            onClick={handlePrevious}
                            disabled={isFirstStep}
                            className={`px-4 py-2 rounded-md font-medium transition ${
                                isFirstStep
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <ChevronLeftIcon className="w-4 h-4" />
                                Previous
                            </div>
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={!isCurrentStepCompleted && !isLastStep && !step.isWelcomeScreen && !step.isCompletionScreen}
                            className={`px-6 py-2 rounded-md font-medium transition flex items-center gap-2 ${
                                !isCurrentStepCompleted && !isLastStep && !step.isWelcomeScreen && !step.isCompletionScreen
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary text-white hover:bg-primary/90'
                            }`}
                            title={!isCurrentStepCompleted && !isLastStep && !step.isWelcomeScreen && !step.isCompletionScreen ? 'Complete this task first' : ''}
                        >
                            {isLastStep ? 'Finish' : 'Next'}
                            {!isLastStep && <ChevronRightIcon className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

